import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  useTheme,
  alpha,
  Stack,
  Avatar,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Pix,
  Description,
  SwapHoriz,
  Refresh,
  ArrowForward,
  AttachMoney,
  Receipt,
  ArrowUpward,
  ArrowDownward,
  Schedule,
  CheckCircleOutline,
  Storage,
  CalendarMonth,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentCompany } from '@/store/slices/authSlice';
import { selectBankAccount, selectBankProvider } from '@/store/slices/bankAccountSlice';
import type { Saldo as BankSaldo, Extrato as BankExtrato, Lancamento as BankLancamento } from '@/types/bank';
import { api } from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import toast from 'react-hot-toast';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, subDays, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ==================== INTERFACES ====================

interface ExtratoTransacao {
  data?: string;
  dataLote?: string;
  tipo: string; // "C" = Crédito, "D" = Débito
  descricao: string;
  valor: string;
  saldoApos?: string;
}

interface ExtratoMensal {
  mes: number;
  ano: number;
  mesLabel: string;
  saldoAtual: string;
  saldoAnterior: string;
  transacoes: ExtratoTransacao[];
  totalEntradas: number;
  totalSaidas: number;
  saldo: number;
}

interface PixTransacao {
  endToEndId: string;
  txid?: string;
  valor: string;
  horario: string;
  infoPagador?: string;
  pagador?: {
    nome?: string;
    cpf?: string;
    cnpj?: string;
  };
}

interface PixMensal {
  mes: number;
  ano: number;
  mesLabel: string;
  transacoes: PixTransacao[];
  totalRecebido: number;
  quantidade: number;
}

interface DashboardCache {
  extratos: ExtratoMensal[];
  pix: PixMensal[];
  lastUpdate: string;
  companyId: string;
}

const CACHE_KEY = 'dashboard_cache';
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes
const MAX_RETRIES = 3;
const RATE_LIMIT_WAIT_MS = 2000; // 2 seconds default wait

// Helper para verificar se é erro de rate limit
const isRateLimitError = (response: any): boolean => {
  if (!response) return false;

  // Check various places where rate limit message might appear
  const message =
    response?.error?.message ||
    response?.message ||
    response?.data?.error?.message ||
    '';

  return message.toLowerCase().includes('rate limit');
};

// Helper para extrair tempo de espera da mensagem de rate limit
const extractWaitTime = (response: any): number => {
  const message =
    response?.error?.message ||
    response?.message ||
    response?.data?.error?.message ||
    '';

  const match = message.match(/Aguarde (\d+) segundos?/i);
  if (match) {
    return parseInt(match[1], 10) * 1000 + 1000; // Add 1s buffer
  }
  return RATE_LIMIT_WAIT_MS;
};

// Helper para fetch com retry em caso de rate limit
const fetchWithRetry = async (
  fetchFn: () => Promise<any>,
  retries = MAX_RETRIES,
  context = ''
): Promise<any> => {
  try {
    const response = await fetchFn();

    // Check for rate limit in response (success: false)
    if (response?.success === false && isRateLimitError(response)) {
      if (retries > 0) {
        const waitTime = extractWaitTime(response);
        console.log(`[${context}] Rate limit hit, waiting ${waitTime}ms before retry (${retries} retries left)...`);
        await new Promise((resolve) => setTimeout(resolve, waitTime));
        return fetchWithRetry(fetchFn, retries - 1, context);
      } else {
        console.warn(`[${context}] Rate limit persisted after all retries`);
      }
    }

    return response;
  } catch (err: any) {
    // Check if error contains rate limit
    const errorData = err?.response?.data || err;
    if (isRateLimitError(errorData) && retries > 0) {
      const waitTime = extractWaitTime(errorData);
      console.log(`[${context}] Rate limit hit (exception), waiting ${waitTime}ms before retry (${retries} retries left)...`);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      return fetchWithRetry(fetchFn, retries - 1, context);
    }
    throw err;
  }
};

// ==================== COMPONENT ====================

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const company = useAppSelector(selectCurrentCompany);

  // Conta bancária selecionada (Empresa · Banco). Sicoob (ou nenhuma) = comportamento histórico abaixo;
  // Efí = dashboard leve via rotas neutras /bank/* (ver bloco "EFÍ" mais adiante).
  const bankAccount = useAppSelector(selectBankAccount);
  const bankProvider = useAppSelector(selectBankProvider);
  const isEfi = bankProvider === 'efi';

  // Efí states
  const [efiSaldo, setEfiSaldo] = useState<BankSaldo | null>(null);
  const [efiExtrato, setEfiExtrato] = useState<BankExtrato | null>(null);
  const [efiLoading, setEfiLoading] = useState(false);
  const [efiError, setEfiError] = useState<string | null>(null);

  // Data states
  const [extratos, setExtratos] = useState<ExtratoMensal[]>([]);
  const [pixData, setPixData] = useState<PixMensal[]>([]);
  const [saldoAtual, setSaldoAtual] = useState<number>(0);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingExtratos, setLoadingExtratos] = useState(false);
  const [loadingPix, setLoadingPix] = useState(false);
  const [progressExtratos, setProgressExtratos] = useState(0);
  const [progressPix, setProgressPix] = useState(0);

  // UI states
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // ==================== CACHE FUNCTIONS ====================

  const loadFromCache = useCallback((): DashboardCache | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data: DashboardCache = JSON.parse(cached);

      // Check if cache is for current company
      if (data.companyId !== company?.id) return null;

      // Check if cache is still valid
      const cacheAge = Date.now() - new Date(data.lastUpdate).getTime();
      if (cacheAge > CACHE_DURATION_MS) return null;

      return data;
    } catch {
      return null;
    }
  }, [company?.id]);

  const saveToCache = useCallback((extratoData: ExtratoMensal[], pixDataToSave: PixMensal[]) => {
    try {
      const cache: DashboardCache = {
        extratos: extratoData,
        pix: pixDataToSave,
        lastUpdate: new Date().toISOString(),
        companyId: company?.id || '',
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (err) {
      console.error('Error saving to cache:', err);
    }
  }, [company?.id]);

  // ==================== FETCH FUNCTIONS ====================

  const fetchExtratoMensal = async (mes: number, ano: number): Promise<ExtratoMensal | null> => {
    try {
      const response = await fetchWithRetry(
        () => api.get(`/conta-corrente/extrato/${mes}/${ano}`),
        MAX_RETRIES,
        `extrato/${mes}/${ano}`
      );

      if (response?.success && response.data) {
        const transacoes = response.data.transacoes || [];

        // Calculate totals based on tipo field (C = Crédito, D = Débito)
        let totalEntradas = 0;
        let totalSaidas = 0;

        transacoes.forEach((t: ExtratoTransacao) => {
          const valor = parseFloat(t.valor) || 0;
          const tipo = (t.tipo || '').toUpperCase();

          if (tipo === 'C' || tipo === 'CREDITO') {
            totalEntradas += valor;
          } else if (tipo === 'D' || tipo === 'DEBITO') {
            totalSaidas += valor;
          }
        });

        const mesLabel = format(new Date(ano, mes - 1), 'MMM/yy', { locale: ptBR });

        return {
          mes,
          ano,
          mesLabel,
          saldoAtual: response.data.saldoAtual || '0',
          saldoAnterior: response.data.saldoAnterior || '0',
          transacoes,
          totalEntradas,
          totalSaidas,
          saldo: totalEntradas - totalSaidas,
        };
      }

      // Check if it was a rate limit that persisted
      if (response?.error?.message?.includes('Rate Limit')) {
        console.warn(`Rate limit persisted for extrato ${mes}/${ano} after retries`);
      }

      return null;
    } catch (err) {
      console.error(`Error fetching extrato ${mes}/${ano}:`, err);
      return null;
    }
  };

  const fetchPixMensal = async (mes: number, ano: number): Promise<PixMensal | null> => {
    try {
      const inicioMes = startOfMonth(new Date(ano, mes - 1));
      const fimMes = endOfMonth(new Date(ano, mes - 1));

      const response = await fetchWithRetry(
        () => api.get('/pix/payments', {
          params: {
            inicio: inicioMes.toISOString(),
            fim: fimMes.toISOString(),
            limit: 1000,
          },
        }),
        MAX_RETRIES,
        `pix/${mes}/${ano}`
      );

      if (response?.success) {
        const transacoes = response.data || [];

        // Calculate totals
        let totalRecebido = 0;
        transacoes.forEach((t: PixTransacao) => {
          totalRecebido += parseFloat(t.valor) || 0;
        });

        const mesLabel = format(new Date(ano, mes - 1), 'MMM/yy', { locale: ptBR });

        return {
          mes,
          ano,
          mesLabel,
          transacoes,
          totalRecebido,
          quantidade: transacoes.length,
        };
      }

      // Check if it was a rate limit that persisted
      if (response?.error?.message?.includes('Rate Limit')) {
        console.warn(`Rate limit persisted for PIX ${mes}/${ano} after retries`);
      }

      return null;
    } catch (err) {
      console.error(`Error fetching PIX ${mes}/${ano}:`, err);
      return null;
    }
  };

  const fetchAllData = useCallback(async (forceRefresh = false) => {
    // Try to load from cache first
    if (!forceRefresh) {
      const cached = loadFromCache();
      if (cached) {
        setExtratos(cached.extratos);
        setPixData(cached.pix);

        // Get current balance from most recent extrato
        if (cached.extratos.length > 0) {
          const latest = cached.extratos[cached.extratos.length - 1];
          setSaldoAtual(parseFloat(latest.saldoAtual) || 0);
        }

        setLoading(false);
        toast.success('Dados carregados do cache');
        return;
      }
    }

    setLoading(true);
    setLoadingExtratos(true);
    setLoadingPix(true);
    setError(null);

    const now = new Date();
    const months: { mes: number; ano: number }[] = [];

    // Generate last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = subMonths(now, i);
      months.push({
        mes: date.getMonth() + 1,
        ano: date.getFullYear(),
      });
    }

    // Fetch extratos
    const extratosResults: ExtratoMensal[] = [];
    for (let i = 0; i < months.length; i++) {
      const { mes, ano } = months[i];
      setProgressExtratos(Math.round(((i + 1) / months.length) * 100));

      const result = await fetchExtratoMensal(mes, ano);
      if (result) {
        extratosResults.push(result);
      }

      // Delay between requests to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setExtratos(extratosResults);
    setLoadingExtratos(false);

    // Get current balance from most recent extrato
    if (extratosResults.length > 0) {
      const latest = extratosResults[extratosResults.length - 1];
      setSaldoAtual(parseFloat(latest.saldoAtual) || 0);
    }

    // Fetch PIX
    const pixResults: PixMensal[] = [];
    for (let i = 0; i < months.length; i++) {
      const { mes, ano } = months[i];
      setProgressPix(Math.round(((i + 1) / months.length) * 100));

      const result = await fetchPixMensal(mes, ano);
      if (result) {
        pixResults.push(result);
      }

      // Delay between requests to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setPixData(pixResults);
    setLoadingPix(false);

    // Save to cache
    saveToCache(extratosResults, pixResults);

    setLoading(false);
    toast.success('Dados atualizados com sucesso!');
  }, [loadFromCache, saveToCache]);

  // ==================== EFÍ (rotas neutras /bank/*) ====================

  const fetchEfiData = useCallback(async () => {
    setEfiLoading(true);
    setEfiError(null);
    const hoje = new Date();
    const inicio = format(subDays(hoje, 29), 'yyyy-MM-dd');
    const fim = format(hoje, 'yyyy-MM-dd');
    const errMsg = (e: any, fallback: string) =>
      e?.response?.data?.error?.message || e?.error?.message || e?.message || fallback;

    const [saldoRes, extratoRes] = await Promise.allSettled([
      api.get<BankSaldo>('/bank/saldo'),
      api.get<BankExtrato>('/bank/extrato', { params: { inicio, fim }, timeout: 60000 }),
    ]);

    const erros: string[] = [];
    if (saldoRes.status === 'fulfilled' && saldoRes.value.success && saldoRes.value.data) {
      setEfiSaldo(saldoRes.value.data);
    } else {
      setEfiSaldo(null);
      erros.push(saldoRes.status === 'fulfilled'
        ? (saldoRes.value.error?.message || 'Erro ao consultar saldo')
        : errMsg(saldoRes.reason, 'Erro ao consultar saldo'));
    }
    if (extratoRes.status === 'fulfilled' && extratoRes.value.success && extratoRes.value.data) {
      setEfiExtrato(extratoRes.value.data);
    } else {
      setEfiExtrato(null);
      erros.push(extratoRes.status === 'fulfilled'
        ? (extratoRes.value.error?.message || 'Erro ao consultar extrato')
        : errMsg(extratoRes.reason, 'Erro ao consultar extrato'));
    }
    if (erros.length) setEfiError(erros.join(' · '));
    setEfiLoading(false);
  }, []);

  useEffect(() => {
    // Sicoob (ou sem conta selecionada): comportamento histórico
    if (!isEfi) fetchAllData();
  }, [isEfi]);

  useEffect(() => {
    if (isEfi) fetchEfiData();
  }, [isEfi, bankAccount?.id, fetchEfiData]);

  // Resumo Efí dos últimos 30 dias (entradas, saídas, por dia, últimos 10 lançamentos)
  const efiResumo = React.useMemo(() => {
    const lancamentos: BankLancamento[] = efiExtrato?.lancamentos ?? [];
    let entradas = 0;
    let saidas = 0;
    const porDia: Record<string, { dia: string; entradas: number; saidas: number }> = {};
    const parse = (s: string): Date | null => {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
      if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const d = parseISO(s || '');
      return isValid(d) ? d : null;
    };
    lancamentos.forEach((l) => {
      const v = Number(l.valor) || 0;
      if (l.tipo === 'C') entradas += v;
      else saidas += v;
      const d = parse(l.data);
      if (d) {
        const key = format(d, 'yyyy-MM-dd');
        if (!porDia[key]) porDia[key] = { dia: format(d, 'dd/MM'), entradas: 0, saidas: 0 };
        if (l.tipo === 'C') porDia[key].entradas += v;
        else porDia[key].saidas += v;
      }
    });
    const chart = Object.keys(porDia).sort().map((k) => porDia[k]);
    const ultimos = [...lancamentos]
      .sort((a, b) => (parse(b.data)?.getTime() || 0) - (parse(a.data)?.getTime() || 0))
      .slice(0, 10);
    return {
      entradas: efiExtrato?.totalCreditos ?? entradas,
      saidas: efiExtrato?.totalDebitos ?? saidas,
      quantidade: lancamentos.length,
      chart,
      ultimos,
      formatData: (s: string) => {
        const d = parse(s);
        if (!d) return s || '-';
        return /^\d{4}-\d{2}-\d{2}$/.test(s) ? format(d, 'dd/MM/yyyy') : format(d, 'dd/MM/yyyy HH:mm');
      },
    };
  }, [efiExtrato]);

  // ==================== COMPUTED VALUES ====================

  // Total dos últimos 12 meses
  const totals = React.useMemo(() => {
    const totalEntradas = extratos.reduce((sum, e) => sum + e.totalEntradas, 0);
    const totalSaidas = extratos.reduce((sum, e) => sum + e.totalSaidas, 0);
    const totalPixRecebido = pixData.reduce((sum, p) => sum + p.totalRecebido, 0);
    const totalPixQtd = pixData.reduce((sum, p) => sum + p.quantidade, 0);

    return {
      entradas: totalEntradas,
      saidas: totalSaidas,
      lucro: totalEntradas - totalSaidas,
      pixRecebido: totalPixRecebido,
      pixQuantidade: totalPixQtd,
    };
  }, [extratos, pixData]);

  // Chart data for extratos
  const chartDataExtratos = React.useMemo(() => {
    return extratos.map((e) => ({
      mes: e.mesLabel,
      entradas: e.totalEntradas,
      saidas: e.totalSaidas,
      saldo: e.saldo,
    }));
  }, [extratos]);

  // Chart data for PIX
  const chartDataPix = React.useMemo(() => {
    return pixData.map((p) => ({
      mes: p.mesLabel,
      recebido: p.totalRecebido,
      quantidade: p.quantidade,
    }));
  }, [pixData]);

  // Recent transactions from all extratos
  const recentTransactions = React.useMemo(() => {
    const allTransacoes: (ExtratoTransacao & { mesAno: string })[] = [];

    extratos.forEach((e) => {
      e.transacoes.forEach((t) => {
        allTransacoes.push({
          ...t,
          mesAno: e.mesLabel,
        });
      });
    });

    // Sort by date descending and take last 10
    return allTransacoes
      .sort((a, b) => {
        const dateA = new Date(a.data || a.dataLote || '1970-01-01');
        const dateB = new Date(b.data || b.dataLote || '1970-01-01');
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 10);
  }, [extratos]);

  // ==================== UI COMPONENTS ====================

  const StatsCard: React.FC<{
    title: string;
    value: number;
    subtitle?: string;
    icon: React.ReactNode;
    color: string;
    trend?: 'up' | 'down';
    /** Texto pronto para exibir no lugar do valor formatado em moeda (ex.: contagem) */
    display?: string;
  }> = ({ title, value, subtitle, icon, color, trend, display }) => (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: 210,
          height: 210,
          background: `linear-gradient(210.04deg, ${alpha(color, 0.3)} -50.94%, rgba(144, 202, 249, 0) 83.49%)`,
          borderRadius: '50%',
          top: -85,
          right: -95,
        },
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} color="text.primary" mb={0.5}>
              {display ?? formatCurrency(value)}
            </Typography>
            {subtitle && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {trend && (
                  <Avatar
                    sx={{
                      width: 20,
                      height: 20,
                      bgcolor: trend === 'up' ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.15),
                    }}
                  >
                    {trend === 'up' ? (
                      <ArrowUpward sx={{ fontSize: 12, color: 'success.main' }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 12, color: 'error.main' }} />
                    )}
                  </Avatar>
                )}
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ width: 44, height: 44, bgcolor: alpha(color, 0.12), color }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, boxShadow: theme.shadows[8], border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color, fontWeight: 500 }}>
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // ==================== EFÍ RENDER (sem rotas Sicoob) ====================

  if (isEfi) {
    const contaLabel = bankAccount ? `${bankAccount.providerLabel} · ${bankAccount.label}` : 'Efí';
    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visão geral dos últimos 30 dias — {contaLabel}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchEfiData}
            disabled={efiLoading}
            sx={{ borderRadius: 2 }}
          >
            {efiLoading ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </Box>

        {efiLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {efiError && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'error.light' }}>
            <Typography variant="body2" color="error.main">{efiError}</Typography>
          </Paper>
        )}

        {!!efiExtrato?.avisos?.length && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            {efiExtrato.avisos.map((a, i) => (
              <Typography key={i} variant="body2" color="text.secondary">{a}</Typography>
            ))}
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* Stats Cards */}
          <Grid item xs={12} sm={6} md={3}>
            {efiLoading && !efiSaldo ? <Skeleton variant="rounded" height={140} /> : (
              <StatsCard
                title="Saldo Disponível"
                value={efiSaldo?.disponivel ?? 0}
                subtitle={efiSaldo?.atualizadoEm ? `Posição: ${efiResumo.formatData(efiSaldo.atualizadoEm)}` : undefined}
                icon={<AccountBalance />}
                color={theme.palette.primary.main}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {efiLoading && !efiExtrato ? <Skeleton variant="rounded" height={140} /> : (
              <StatsCard
                title="Entradas (30d)"
                value={efiResumo.entradas}
                subtitle="Últimos 30 dias"
                icon={<TrendingUp />}
                color={theme.palette.success.main}
                trend="up"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {efiLoading && !efiExtrato ? <Skeleton variant="rounded" height={140} /> : (
              <StatsCard
                title="Saídas (30d)"
                value={efiResumo.saidas}
                subtitle="Últimos 30 dias"
                icon={<TrendingDown />}
                color={theme.palette.error.main}
                trend="down"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {efiLoading && !efiExtrato ? <Skeleton variant="rounded" height={140} /> : (
              <StatsCard
                title="Lançamentos (30d)"
                value={efiResumo.quantidade}
                display={String(efiResumo.quantidade)}
                subtitle={`Líquido ${formatCurrency(efiResumo.entradas - efiResumo.saidas)}`}
                icon={<Receipt />}
                color={theme.palette.info.main}
              />
            )}
          </Grid>

          {/* Movimento diário */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Movimento Diário - Últimos 30 Dias
                </Typography>
                {efiResumo.chart.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      {efiLoading ? 'Carregando...' : 'Sem lançamentos no período'}
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={efiResumo.chart}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                      <XAxis
                        dataKey="dia"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                        tickFormatter={(value) => (Math.abs(value) >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(value))}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="entradas" name="Entradas" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="saidas" name="Saídas" fill={theme.palette.error.main} radius={[4, 4, 0, 0]} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Últimos lançamentos */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Últimos Lançamentos
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/conta/extrato')}
                  >
                    Ver Extrato Completo
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Contraparte</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {efiResumo.ultimos.map((l, index) => {
                        const isCredito = l.tipo === 'C';
                        return (
                          <TableRow key={l.id || index} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Typography variant="body2">{efiResumo.formatData(l.data)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={isCredito ? 'Crédito' : 'Débito'}
                                size="small"
                                color={isCredito ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                {l.descricao || '-'}
                              </Typography>
                              {l.categoria && (
                                <Typography variant="caption" color="text.secondary">{l.categoria}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                                {l.contraparteNome || '-'}
                              </Typography>
                              {l.contraparteDocumento && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                  {l.contraparteDocumento}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ color: isCredito ? 'success.main' : 'error.main' }}
                              >
                                {isCredito ? '+' : '-'}
                                {formatCurrency(Number(l.valor) || 0)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {efiResumo.ultimos.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              {efiLoading ? 'Carregando...' : 'Nenhum lançamento encontrado'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // ==================== LOADING STATE ====================

  if (loading && extratos.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Carregando dados dos últimos 12 meses...
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Stack spacing={3}>
                {loadingExtratos && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Carregando extratos bancários...
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {progressExtratos}%
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progressExtratos} />
                  </Box>
                )}

                {loadingPix && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Carregando transações PIX...
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {progressPix}%
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progressPix} color="secondary" />
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>

          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // ==================== MAIN RENDER ====================

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visão geral dos últimos 12 meses
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Dados salvos localmente">
            <Chip
              icon={<Storage sx={{ fontSize: 16 }} />}
              label="Cache ativo"
              size="small"
              color="info"
              variant="outlined"
            />
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => fetchAllData(true)}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </Stack>
      </Box>

      {/* Loading indicator for background refresh */}
      {loading && (extratos.length > 0 || pixData.length > 0) && (
        <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
      )}

      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Saldo Atual"
            value={saldoAtual}
            icon={<AccountBalance />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Entradas (12m)"
            value={totals.entradas}
            subtitle="Últimos 12 meses"
            icon={<TrendingUp />}
            color={theme.palette.success.main}
            trend="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Saídas (12m)"
            value={totals.saidas}
            subtitle="Últimos 12 meses"
            icon={<TrendingDown />}
            color={theme.palette.error.main}
            trend="down"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="PIX Recebidos (12m)"
            value={totals.pixRecebido}
            subtitle={`${totals.pixQuantidade} transações`}
            icon={<Pix />}
            color={theme.palette.info.main}
          />
        </Grid>

        {/* Tabs for Charts */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab icon={<AccountBalance />} iconPosition="start" label="Extrato Mensal" />
                <Tab icon={<Pix />} iconPosition="start" label="PIX Mensal" />
              </Tabs>

              {/* Extrato Chart */}
              {activeTab === 0 && (
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Fluxo de Caixa - Últimos 12 Meses
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={chartDataExtratos}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                      <XAxis
                        dataKey="mes"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="entradas" name="Entradas" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="saidas" name="Saídas" fill={theme.palette.error.main} radius={[4, 4, 0, 0]} />
                      <Line
                        type="monotone"
                        dataKey="saldo"
                        name="Saldo"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        dot={{ fill: theme.palette.primary.main }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* PIX Chart */}
              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    PIX Recebidos - Últimos 12 Meses
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={chartDataPix}>
                      <defs>
                        <linearGradient id="colorPix" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.palette.info.main} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={theme.palette.info.main} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                      <XAxis
                        dataKey="mes"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="recebido"
                        name="PIX Recebido"
                        stroke={theme.palette.info.main}
                        strokeWidth={2}
                        fill="url(#colorPix)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Summary Tables */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Resumo Mensal - Extratos
                </Typography>
                <Chip
                  icon={<CalendarMonth sx={{ fontSize: 16 }} />}
                  label={`${extratos.length} meses`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Mês</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>Entradas</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>Saídas</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {extratos.slice().reverse().map((e) => (
                      <TableRow key={`${e.mes}-${e.ano}`} hover>
                        <TableCell>{e.mesLabel}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          {formatCurrency(e.totalEntradas)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          {formatCurrency(e.totalSaidas)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, color: e.saldo >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {formatCurrency(e.saldo)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Resumo Mensal - PIX
                </Typography>
                <Chip
                  icon={<Pix sx={{ fontSize: 16 }} />}
                  label={`${totals.pixQuantidade} transações`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Box>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Mês</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Quantidade</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'info.main' }}>Total Recebido</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Média</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pixData.slice().reverse().map((p) => (
                      <TableRow key={`${p.mes}-${p.ano}`} hover>
                        <TableCell>{p.mesLabel}</TableCell>
                        <TableCell align="right">{p.quantidade}</TableCell>
                        <TableCell align="right" sx={{ color: 'info.main', fontWeight: 600 }}>
                          {formatCurrency(p.totalRecebido)}
                        </TableCell>
                        <TableCell align="right">
                          {p.quantidade > 0 ? formatCurrency(p.totalRecebido / p.quantidade) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Transactions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Últimas Transações
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/conta/extrato')}
                >
                  Ver Extrato Completo
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentTransactions.map((t, index) => {
                      const dataTransacao = t.data || t.dataLote;
                      const tipo = (t.tipo || '').toUpperCase();
                      const isCredito = tipo === 'C' || tipo === 'CREDITO';
                      const valor = parseFloat(t.valor) || 0;

                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {dataTransacao ? format(new Date(dataTransacao), 'dd/MM/yyyy') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isCredito ? 'Crédito' : 'Débito'}
                              size="small"
                              color={isCredito ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                              {t.descricao || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                color: isCredito ? 'success.main' : 'error.main',
                              }}
                            >
                              {isCredito ? '+' : '-'}
                              {formatCurrency(valor)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {recentTransactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">Nenhuma transação encontrada</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
